import { useState, useEffect, useCallback } from "react";
import { Phone, Mail, MessageCircle, Download, LogOut, Send, Lock } from "lucide-react";
import { business } from "../content/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mf_panel_token";

const inputClass =
  "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-[15px] text-ink placeholder:text-slate-500 focus:border-cadastral focus:outline-none focus:ring-1 focus:ring-cadastral transition-colors";

const estadoStyle = {
  nuevo: "bg-terracotta/10 text-terracotta-dark border-terracotta/30",
  contactado: "bg-cadastral/10 text-cadastral border-cadastral/30",
  cerrado: "bg-precision/10 text-precision border-precision/30",
};

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...(options.body ? { "Content-Type": "application/json" } : {}), Authorization: `Bearer ${token}`, ...options.headers },
  });
  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
    throw new Error("Sesión caducada");
  }
  return res;
};

function LeadCard({ lead, onChanged }) {
  const [open, setOpen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState("");
  const fecha = new Date(lead.created_at).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" });
  const whatsappLead = `https://wa.me/34${lead.telefono.replace(/[^0-9]/g, "").replace(/^34/, "")}?text=${encodeURIComponent(`Hola ${lead.nombre}, te escribo de MideFinca en relación a tu consulta: ${lead.necesidad}.`)}`;

  const setEstado = async (estado) => {
    await apiFetch(`/panel/leads/${lead.id}`, { method: "PATCH", body: JSON.stringify({ estado }) });
    onChanged();
  };

  const downloadFile = async () => {
    const res = await apiFetch(`/panel/leads/${lead.id}/archivo`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = lead.archivo?.filename || "adjunto";
    a.click();
    URL.revokeObjectURL(url);
  };

  const responder = async () => {
    setSending(true);
    setFeedback("");
    try {
      const res = await apiFetch(`/panel/leads/${lead.id}/responder`, { method: "POST", body: JSON.stringify({ mensaje }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Error al enviar");
      setFeedback("Respuesta enviada por email.");
      setMensaje("");
      onChanged();
    } catch (e) {
      setFeedback(e.message);
    }
    setSending(false);
  };

  return (
    <div className="card-technical p-5 sm:p-6" data-testid={`lead-card-${lead.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <button onClick={() => setOpen(!open)} className="text-left flex-1 min-w-0" data-testid={`lead-toggle-${lead.id}`}>
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="text-base font-semibold text-ink">{lead.nombre}</h3>
            <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 border rounded-sm ${estadoStyle[lead.estado] || estadoStyle.nuevo}`}>
              {lead.estado}
            </span>
            {lead.respuestas_count > 0 && (
              <span className="text-[11px] font-mono text-slate-400">{lead.respuestas_count} respuesta{lead.respuestas_count > 1 ? "s" : ""}</span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">{lead.necesidad} · {lead.municipio} · {fecha}</p>
        </button>
        <select
          value={lead.estado}
          onChange={(e) => setEstado(e.target.value)}
          data-testid={`lead-estado-${lead.id}`}
          className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white"
          aria-label="Estado de la consulta"
        >
          <option value="nuevo">Nuevo</option>
          <option value="contactado">Contactado</option>
          <option value="cerrado">Cerrado</option>
        </select>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <a href={`tel:${lead.telefono}`} className="btn-secondary !px-3.5 !py-2 !text-xs" data-testid={`lead-call-${lead.id}`}>
          <Phone size={13} aria-hidden="true" /> {lead.telefono}
        </a>
        <a href={whatsappLead} target="_blank" rel="noopener noreferrer" className="btn-secondary !px-3.5 !py-2 !text-xs" data-testid={`lead-whatsapp-${lead.id}`}>
          <MessageCircle size={13} aria-hidden="true" /> WhatsApp
        </a>
        {lead.email && (
          <a href={`mailto:${lead.email}`} className="btn-secondary !px-3.5 !py-2 !text-xs" data-testid={`lead-email-${lead.id}`}>
            <Mail size={13} aria-hidden="true" /> {lead.email}
          </a>
        )}
        {lead.has_archivo && (
          <button onClick={downloadFile} className="btn-secondary !px-3.5 !py-2 !text-xs" data-testid={`lead-file-${lead.id}`}>
            <Download size={13} aria-hidden="true" /> Adjunto
          </button>
        )}
      </div>

      {open && (
        <div className="mt-5 border-t hairline pt-5 space-y-4">
          <div>
            <p className="tech-label !text-[10px] mb-1.5">Caso</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{lead.mensaje}</p>
          </div>
          {lead.referencia_catastral && (
            <p className="text-sm"><span className="tech-label !text-[10px] mr-2">Ref. catastral</span><span className="font-mono text-ink">{lead.referencia_catastral}</span></p>
          )}
          {lead.email ? (
            <div>
              <p className="tech-label !text-[10px] mb-1.5">Responder por email desde el panel</p>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                rows={3}
                className={inputClass}
                placeholder={`Respuesta para ${lead.nombre}…`}
                data-testid={`lead-reply-${lead.id}`}
              />
              <div className="mt-2 flex items-center gap-3">
                <button onClick={responder} disabled={sending || mensaje.trim().length < 2} className="btn-primary !px-4 !py-2 !text-xs disabled:opacity-50" data-testid={`lead-send-${lead.id}`}>
                  {sending ? "Enviando…" : (<><Send size={13} aria-hidden="true" /> Enviar email</>)}
                </button>
                {feedback && <p className="text-xs text-slate-600" role="status">{feedback}</p>}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">Este contacto no dejó email: respóndele por teléfono o WhatsApp.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Panel() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch("/panel/leads");
      setLeads(await res.json());
    } catch (e) {
      setError("No se pudieron cargar las consultas.");
    }
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setSending(true);
    setLoginError("");
    const form = e.target;
    try {
      const res = await fetch(`${API}/panel/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.value, password: form.password.value }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Error de acceso");
      localStorage.setItem(TOKEN_KEY, body.token);
      setToken(body.token);
    } catch (err) {
      setLoginError(err.message);
    }
    setSending(false);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
  };

  return (
    <section className="container-site section-gap" data-testid="panel-page">
      <meta name="robots" content="noindex, nofollow" />
      {!token ? (
        <div className="max-w-md mx-auto">
          <Lock size={28} className="text-terracotta" aria-hidden="true" />
          <h1 className="mt-4 text-2xl sm:text-3xl font-bold">Panel de consultas</h1>
          <p className="mt-2 text-sm text-slate-600">Acceso privado para la gestión de encargos de MideFinca.</p>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="panel-login-form">
            <div>
              <label htmlFor="pl-email" className="block text-sm font-medium text-ink mb-1.5">Email</label>
              <input id="pl-email" name="email" type="email" required className={inputClass} data-testid="panel-login-email" autoComplete="username" />
            </div>
            <div>
              <label htmlFor="pl-password" className="block text-sm font-medium text-ink mb-1.5">Contraseña</label>
              <input id="pl-password" name="password" type="password" required className={inputClass} data-testid="panel-login-password" autoComplete="current-password" />
            </div>
            {loginError && <p role="alert" data-testid="panel-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{loginError}</p>}
            <button type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60" data-testid="panel-login-submit">
              {sending ? "Entrando…" : "Entrar"}
            </button>
          </form>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Panel de consultas</h1>
              <p className="mt-1 text-sm text-slate-500">{leads ? `${leads.length} consulta${leads.length === 1 ? "" : "s"}` : "Cargando…"}</p>
            </div>
            <button onClick={logout} className="btn-secondary !px-4 !py-2 !text-xs" data-testid="panel-logout">
              <LogOut size={13} aria-hidden="true" /> Salir
            </button>
          </div>
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <div className="space-y-4" data-testid="panel-leads-list">
            {leads && leads.length === 0 && (
              <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-sm p-8 text-center">
                Todavía no hay consultas. Cuando alguien envíe el formulario, aparecerá aquí aunque el email falle.
              </p>
            )}
            {leads && leads.map((lead) => <LeadCard key={lead.id} lead={lead} onChanged={load} />)}
          </div>
        </div>
      )}
    </section>
  );
}
